import { GuardianLoanAction } from 'app/components/guardian';
import { LoanFeePaymentDetails } from 'app/components/loan-fee-payment-details';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';
import React from 'react';
import type { Loan } from 'app/types/loan';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';

import { canUserCompleteLoan, LoanETA } from 'app/types/loan';
import { Separator } from 'app/design/primitives/text-divider';
import { Label } from 'app/design/icons/label';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import { useNavigation } from 'app/hooks/navigation';
import type { User } from 'app/types/user';
import { getLoanInfos, getTranslationSuffix } from '../functions';
import { useUser } from 'app/network/user';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { LoanFeeModals, useLoanFeeModals } from 'app/screens/management/loan-fee';
import { Clubs } from 'app/design/icons/clubs';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCalendar, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
import { ActionRow } from './action-row';
import { canAssignGuardian } from 'app/lib/guardian';
const theme = createDSTheme();

interface DetailsSectionProps {
  loan?: Loan | null;
  user?: User;
}

function ParticipantsSection({
  loan,
  isLender,
  t,
}: {
  loan?: Loan | null;
  isLender: boolean;
  t: TFunction<'one-loan'>;
}) {
  if (
    !loan?.listing.maxParticipants ||
    loan.listing.numParticipants === undefined ||
    loan.listing.numParticipants === null
  ) {
    return null;
  }

  return (
    <Row>
      <Column gap="spacing.general.x2">
        <Text fontWeight="700" variant="bodyBase">
          {t(isLender ? 'myParticipations' : 'allParticipations')}
        </Text>
        <Row gap="spacing.general.x1" alignItems="center">
          {isLender ? (
            <Text style={{ color: theme.colors['text.secondary'] }} variant="bodyBase">
              {loan.numParticipants}
            </Text>
          ) : (
            <>
              <Text
                color={loan.listing.numParticipants > 0 ? 'text.primary' : 'text.primary'}
                style={{
                  backgroundColor:
                    loan.listing.numParticipants > 0
                      ? theme.colors['surface.brand.secondary-1.default']
                      : theme.colors['surface.brand.secondary-2.default'],
                  paddingHorizontal: theme.spacing['spacing.general.x2'],
                  paddingVertical: 4,
                  borderRadius: theme.borderRadii['radius.sm'],
                }}
              >
                {loan.listing.numParticipants >= loan.listing.maxParticipants
                  ? t('subscriptionsSection.participantsFull', { ns: 'one-listing' })
                  : t('subscriptionsSection.participants', {
                      ns: 'one-listing',
                      count: loan.listing.numParticipants,
                    })}
              </Text>
              <Text style={{ color: theme.colors['text.secondary'] }} variant="bodyBase">
                {`/ ${loan.listing.maxParticipants.toFixed(0)}`}
              </Text>
            </>
          )}
        </Row>
      </Column>
    </Row>
  );
}

function LoanFeeSection({ loan, onPress, t }: { loan?: Loan | null; onPress: () => void; t: TFunction<'one-loan'> }) {
  if (!loan?.fee && !loan?.feeDocument) {
    return null;
  }
  if (loan.feeDocument) {
    return <LoanFeePaymentDetails document={loan.feeDocument} showPaymentMethod={false} />;
  }

  return (
    <Pressable onPress={onPress}>
      <Column
        gap="spacing.general.x0,5"
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          padding: theme.spacing['spacing.general.x4'],
          borderRadius: theme.borderRadii['radius.lg'],
        }}
      >
        <Row alignItems="center" gap="spacing.general.x0,5">
          <Text fontWeight="700" variant="bodyBase">
            {t('loanFee.title', {
              ns: 'one-listing',
              amount: t('x$', { ns: 'common', amount: loan?.fee?.toFixed(loan.fee % 1 === 0 ? 0 : 2) }),
            })}
          </Text>
          <View style={{ marginVertical: -8 }}>
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
          </View>
        </Row>
        {loan.feeDescription ? <Text variant="bodyBase">{loan.feeDescription}</Text> : null}
        <LoanFeePaymentDetails document={loan.feeDocument} paymentMethod={loan.feePaymentMethod} />
      </Column>
    </Pressable>
  );
}

function CounterpartAction({
  isLender,
  loan,
  onOpenChat,
  onOpenParticipants,
  otherUser,
  t,
}: {
  isLender: boolean;
  loan?: Loan | null;
  onOpenChat: () => void;
  onOpenParticipants: () => void;
  otherUser?: Loan['borrower'];
  t: TFunction<'one-loan'>;
}) {
  if (loan?.listing.maxParticipants && !isLender) {
    return <ActionRow onPress={onOpenParticipants} prefix={<Clubs />} text={t('listParticipants')} />;
  }

  if (!otherUser) {
    return null;
  }

  return (
    <ActionRow
      onPress={onOpenChat}
      prefix={
        otherUser.profile ? (
          <PCImage
            aspectRatio={1}
            height={30}
            source={{ uri: otherUser.profile.picture }}
            style={{ borderRadius: 15 }}
          />
        ) : (
          <ChatBubble />
        )
      }
      text={t('sendMessage', {
        name: !otherUser.profile
          ? t('deletedUser', { ns: 'common' })
          : `${otherUser.profile.firstName} ${otherUser.profile.lastName}`,
      })}
    />
  );
}

function LoanGuardianAssignment({ loan, userId }: Pick<DetailsSectionProps, 'loan'> & { userId?: User['id'] }) {
  if (!loan || !canAssignGuardian(loan, userId)) return null;
  return <GuardianLoanAction loan={loan} userId={userId} />;
}

export function Details({ loan, user }: DetailsSectionProps) {
  const { t } = useTranslation('one-loan');
  const navigation = useNavigation();
  const { data: myself } = useUser();

  const { loanStatus, otherUser, loanType } = (() => {
    return getLoanInfos(loan, user);
  })();

  const isLender = loan?.lenderId === myself?.id;

  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;

  const canChangeDates = Boolean(loan && canUserCompleteLoan(loan, isLender) && loanStatus !== LoanETA.returned);
  const tSuffix = getTranslationSuffix({ isService, isDonation, isSell, loanType });

  const loanFeeModalsState = useLoanFeeModals();

  const openParticipants = () => {
    if (loan) {
      navigation.navigate('loan-participants', { loanId: loan.id }, { pop: true });
    }
  };
  const openChat = () => {
    if (loan) {
      navigateToChat(navigation, loan.channelURL);
    }
  };
  const showDatesPicker = () => {
    if (!loan) return;
    navigation.navigate(
      'loan-dates-picker',
      {
        loanId: loan.id,
        url: loan.channelURL,
        promptIntroduceYourself: false,
        goBackTo: 'one-loan',
      },
      { pop: true },
    );
  };

  const participantSectionIsNull =
    !loan?.listing.maxParticipants ||
    loan.listing.numParticipants === undefined ||
    loan.listing.numParticipants === null;
  const loanFeeSectionIsNull = !loan?.fee && !loan?.feeDocument;

  return (
    <Column gap="spacing.general.x4">
      {participantSectionIsNull ? null : <ParticipantsSection loan={loan} isLender={isLender} t={t} />}

      {loanFeeSectionIsNull ? null : (
        <LoanFeeSection loan={loan} onPress={() => loanFeeModalsState.setIsLoanFeeOtherModalOpen(true)} t={t} />
      )}

      {!loanFeeSectionIsNull || !participantSectionIsNull ? (
        <Separator color={theme.colors['surface.tertiary-neutral']} />
      ) : null}

      <ActionRow
        onPress={() => {
          if (loan) navigateToListing(navigation, loan.listing.id);
        }}
        prefix={<Label />}
        text={t('viewListing')}
      />

      {otherUser ? <Separator color={theme.colors['surface.tertiary-neutral']} /> : null}

      {otherUser ? (
        <CounterpartAction
          isLender={isLender}
          loan={loan}
          onOpenChat={openChat}
          onOpenParticipants={openParticipants}
          otherUser={otherUser}
          t={t}
        />
      ) : null}

      {canChangeDates ? <Separator color={theme.colors['surface.tertiary-neutral']} /> : null}

      {canChangeDates ? (
        <ActionRow
          onPress={showDatesPicker}
          prefix={<IconCalendar strokeWidth={ICON_STROKE_WIDTH} />}
          text={t(`changeDatesButtonText.${tSuffix}`)}
        />
      ) : null}
      <LoanGuardianAssignment loan={loan} userId={myself?.id} />
      {loanFeeSectionIsNull ? null : <LoanFeeModals state={loanFeeModalsState} />}
    </Column>
  );
}
